import * as React from 'react';
import {
  WideBevelSurface,
  WideBevelSurfaceFinish,
  getWideBevelSampling,
  bevelSoftenSigma,
  usesNativeBevelSurface,
} from './wideBevelSurface';
import type { BevelRenderSettings } from '../utils/getBevelDepth';
import { TextEffectMaskedBlend } from './textEffectBlend';
import { effectContourTable } from '../utils/textEffectContours';
import { textEffectFilterRegion } from './textEffectFilterRegion';
import { getBevelSurface } from './bevelSurface';
import { SubpixelBevelCoverage } from './subpixelBevelCoverage';
import { useBevelSurfaceCache } from './bevelSurfaceCache';
import {
  getNormalBevelSampling,
  NormalBevelLighting,
} from './normalBevelSampling';

type Props = {
  readonly id: string;
  readonly depth: BevelRenderSettings;
  readonly width: number;
  readonly height: number;
  readonly filterRef?: React.Ref<SVGFilterElement>;
  readonly cachePreview?: boolean;
};

const HAS_COVERAGE = [0, ...Array(255).fill(1)].join(' ');
const IS_TRANSLUCENT = [...Array(255).fill(1), 0].join(' ');

const maskForBlend = (
  intensity: boolean,
  soften: number,
  kind: 'Highlight' | 'Shadow'
) =>
  !intensity
    ? `covered${kind}`
    : soften
    ? `soft${kind}Mask`
    : `${kind.toLowerCase()}Mask`;

const usesIntensityBlend = (mode: string, useIntensity: boolean) =>
  useIntensity && ['color-dodge', 'linear-burn'].includes(mode);

// Light a reconstructed surface, but paint only the authored inner bevel.
// Fractional morphology rounds to pixel-sized steps in Chrome. Continuous
// coverage avoids those steps without blurring or expanding the final glyph.
export function BevelDepthFilter({
  id,
  depth,
  width,
  height,
  filterRef,
  cachePreview = false,
}: Props) {
  const { technique, size, softness, contour, contourRange } = depth;
  const softenSigma = bevelSoftenSigma(depth);
  const rawDepth = softenSigma ? { ...depth, softness: 0 } : depth;
  const [pixelRatio, setPixelRatio] = React.useState(() =>
    typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1
  );
  React.useEffect(() => {
    const update = () => setPixelRatio(window.devicePixelRatio || 1);
    const resolution = window.matchMedia?.(`(resolution: ${pixelRatio}dppx)`);
    resolution?.addEventListener?.('change', update);
    window.addEventListener('resize', update);
    return () => {
      resolution?.removeEventListener?.('change', update);
      window.removeEventListener('resize', update);
    };
  }, [pixelRatio]);
  const wideSampling = getWideBevelSampling(rawDepth, pixelRatio);
  const preserveInterior = usesNativeBevelSurface(depth);
  const localFilterRef = React.useRef<SVGFilterElement | null>(null);
  const setFilterRef = React.useCallback(
    (node: SVGFilterElement | null) => {
      localFilterRef.current = node;
      if (typeof filterRef === 'function') filterRef(node);
      else if (filterRef)
        (filterRef as React.MutableRefObject<SVGFilterElement | null>).current =
          node;
    },
    [filterRef]
  );
  const cachedSurface = useBevelSurfaceCache(
    localFilterRef,
    cachePreview && Boolean(wideSampling),
    [
      id,
      technique,
      size,
      softness,
      contour,
      contourRange,
      width,
      height,
      pixelRatio,
    ].join(':')
  );
  // Preserve the previous Dodge/Burn appearance outside native calibration.
  // Fractional authored sizes
  // retain their prior coverage-gated surface and post-blend masking, even
  // when an export scales them up to one painted pixel or more.
  const useIntensity = depth.documentSize >= 1;
  // Prototype: carry nonlinear effect intensity through the surface's lighting
  // footprint. Clipping it again to the geometric inset removes the raised rim.
  const intensityHighlight = usesIntensityBlend(
    depth.highlightBlendMode,
    useIntensity
  );
  const intensityShadow = usesIntensityBlend(
    depth.shadowBlendMode,
    useIntensity
  );

  const surface = React.useMemo(
    () =>
      getBevelSurface({
        technique,
        size,
        softness: softenSigma ? 0 : softness,
        contour,
        contourRange,
      }),
    [technique, size, softness, contour, contourRange, softenSigma]
  );
  const normalSampling = getNormalBevelSampling(rawDepth, pixelRatio);
  // Include normal averaging, its fractional offset, the light derivative and
  // paint-region erosion when extending restoration across an opaque endpoint.
  const restorationRadius =
    softenSigma * 3 +
    (normalSampling
      ? (Math.ceil(normalSampling.order / 2) +
          Math.ceil(normalSampling.samplingScale) +
          2) /
        pixelRatio
      : size + 1 / pixelRatio);

  const padding =
    depth.size +
    Math.max(surface.sigma, surface.surfaceSigma, softenSigma) * 3 +
    2;
  const flat = Math.sin((depth.altitude * Math.PI) / 180);
  const isLinearGloss = depth.glossContour === 'linear';
  const highlightSlope =
    (isLinearGloss ? depth.highlightOpacity : 1) / Math.max(0.001, 1 - flat);
  const shadowSlope =
    (isLinearGloss ? depth.shadowOpacity : 1) / Math.max(0.001, flat);
  return (
    <filter
      ref={setFilterRef}
      data-cached-bevel-surface={cachedSurface ? 'ready' : undefined}
      id={id}
      {...textEffectFilterRegion(width, height, padding)}
      colorInterpolationFilters="sRGB"
    >
      <feMorphology
        in="SourceAlpha"
        operator="dilate"
        radius={surface.sigma * 3 + depth.size}
        result="localAlpha"
      />
      {preserveInterior && (
        <>
          {/* Restore paint beyond the bevel/Soften footprint for translucent
              fills. Keep the original surface reconstruction and opaque
              calibration, including antialiased boundaries. */}
          <feComponentTransfer in="SourceAlpha" result="solidSilhouette">
            <feFuncA type="discrete" tableValues={HAS_COVERAGE} />
          </feComponentTransfer>
          <feMorphology
            in="solidSilhouette"
            operator="erode"
            radius={Math.max(1 / pixelRatio, size + softenSigma * 3)}
            result="solidInterior"
          />
          <feComponentTransfer in="SourceAlpha" result="translucentPaint">
            <feFuncA type="discrete" tableValues={IS_TRANSLUCENT} />
          </feComponentTransfer>
          <feMorphology
            in="translucentPaint"
            operator="erode"
            radius={1 / pixelRatio}
            result="translucentPaintRegion"
          />
          <feComposite
            in="solidInterior"
            in2="translucentPaintRegion"
            operator="in"
            result="translucentFlatPaint"
          />
          <feMorphology
            in="translucentFlatPaint"
            operator="dilate"
            radius={restorationRadius}
            result="translucentRegion"
          />
          <feComposite
            in="solidInterior"
            in2="translucentRegion"
            operator="in"
            result="translucentInterior"
          />
        </>
      )}
      <feGaussianBlur
        in="SourceAlpha"
        stdDeviation={surface.sigma}
        result="smoothAlpha"
      />
      <feColorMatrix
        in="localAlpha"
        values="0 0 0 -1 1  0 0 0 -1 1  0 0 0 -1 1  0 0 0 0 1"
        result="inverseLocalAlpha"
      />
      <feColorMatrix
        in="smoothAlpha"
        values="0 0 0 1 0  0 0 0 1 0  0 0 0 1 0  0 0 0 0 1"
        result="coverageColor"
      />
      {/* Dodge divides blurred coverage by local fill alpha, without changing
          the source fill or its transparency. Zero-alpha output is masked. */}
      <feBlend
        in="inverseLocalAlpha"
        in2="coverageColor"
        mode="color-dodge"
        result="normalizedCoverageColor"
      />
      <feColorMatrix
        in="normalizedCoverageColor"
        values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  1 0 0 0 0"
        result="normalizedCoverage"
      />
      <feComponentTransfer in="normalizedCoverage" result="heightRamp">
        <feFuncA type="table" tableValues={surface.height} />
      </feComponentTransfer>
      <feComponentTransfer in="heightRamp" result="contouredHeight">
        <feFuncA type="table" tableValues={surface.contour} />
      </feComponentTransfer>
      {/* Keep narrow strokes visible without sending their fallback height
          through a high-contrast contour, which exaggerates subpixel edges. */}
      <feComposite
        in="contouredHeight"
        in2="SourceAlpha"
        operator="arithmetic"
        k2={1}
        k3={1 / 16}
        result="heightSurface"
      />
      {cachedSurface && wideSampling ? (
        <>
          <feImage
            href={cachedSurface.uri}
            x={cachedSurface.x}
            y={cachedSurface.y}
            width={cachedSurface.width}
            height={cachedSurface.height}
            preserveAspectRatio="none"
            result="cachedWideDistance"
          />
          <WideBevelSurfaceFinish
            sampling={wideSampling}
            input="cachedWideDistance"
          />
        </>
      ) : wideSampling ? (
        <WideBevelSurface sampling={wideSampling} />
      ) : normalSampling ? (
        <feConvolveMatrix
          in="SourceAlpha"
          order={normalSampling.order}
          kernelMatrix={normalSampling.kernel}
          divisor={1}
          edgeMode="none"
          result="surface"
        />
      ) : (
        <feGaussianBlur
          in="heightSurface"
          stdDeviation={surface.surfaceSigma}
          result="surface"
        />
      )}
      <feDiffuseLighting
        in="surface"
        surfaceScale={
          wideSampling?.surfaceScale ??
          normalSampling?.surfaceScale ??
          depth.surfaceScale
        }
        diffuseConstant={1}
        lightingColor="white"
        result={normalSampling ? 'primaryLighting' : 'lighting'}
      >
        <feDistantLight azimuth={depth.azimuth} elevation={depth.altitude} />
      </feDiffuseLighting>
      {normalSampling && (
        <NormalBevelLighting
          surfaceScale={normalSampling.surfaceScale}
          azimuth={depth.azimuth}
          altitude={depth.altitude}
          offsetX={normalSampling.offsetX}
          offsetY={normalSampling.offsetY}
          samplingScale={normalSampling.samplingScale}
        />
      )}
      <feComponentTransfer in="normalizedCoverage" result="insetCoverage">
        <feFuncA type="table" tableValues={surface.inset} />
      </feComponentTransfer>
      <feComposite
        in="insetCoverage"
        in2="localAlpha"
        operator="in"
        result="insetAlpha"
      />
      <feColorMatrix
        in="SourceAlpha"
        values="0 0 0 -1 1  0 0 0 -1 1  0 0 0 -1 1  0 0 0 0 1"
        result="inverseSourceAlpha"
      />
      <feColorMatrix
        in="insetAlpha"
        values="0 0 0 1 0  0 0 0 1 0  0 0 0 1 0  0 0 0 0 1"
        result="insetAlphaColor"
      />
      <feBlend
        in="inverseSourceAlpha"
        in2="insetAlphaColor"
        mode="color-dodge"
        result="insetRatio"
      />
      <feColorMatrix
        in="insetRatio"
        values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  -1 0 0 0 1"
        result={size < 0.5 ? 'distanceBevelCoverage' : 'bevelCoverage'}
      />
      {size < 0.5 && <SubpixelBevelCoverage size={size} />}
      <feColorMatrix
        in="lighting"
        type="matrix"
        values={`0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  ${highlightSlope} 0 0 0 ${
          -flat * highlightSlope
        }`}
        result={isLinearGloss ? 'highlightMask' : 'highlightResponse'}
      />
      <feColorMatrix
        in="lighting"
        type="matrix"
        values={`0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  ${-shadowSlope} 0 0 0 ${
          flat * shadowSlope
        }`}
        result={isLinearGloss ? 'shadowMask' : 'shadowResponse'}
      />
      {!isLinearGloss && (
        <>
          <feComponentTransfer in="highlightResponse" result="highlightMask">
            <feFuncA
              type="table"
              tableValues={effectContourTable(
                depth.glossContour,
                depth.highlightOpacity
              )}
            />
          </feComponentTransfer>
          <feComponentTransfer in="shadowResponse" result="shadowMask">
            <feFuncA
              type="table"
              tableValues={effectContourTable(
                depth.glossContour,
                depth.shadowOpacity
              )}
            />
          </feComponentTransfer>
        </>
      )}
      {softenSigma > 0 && (
        <>
          <feGaussianBlur
            in="highlightMask"
            stdDeviation={softenSigma}
            result="softHighlightMask"
          />
          <feGaussianBlur
            in="shadowMask"
            stdDeviation={softenSigma}
            result="softShadowMask"
          />
        </>
      )}
      <feComponentTransfer in="SourceGraphic" result="bevelBase">
        <feFuncA type="table" tableValues="1 1" />
      </feComponentTransfer>
      <feComposite
        in="highlightMask"
        in2="bevelCoverage"
        operator="in"
        result="coveredHighlight"
      />
      <TextEffectMaskedBlend
        useIntensity={useIntensity}
        color={depth.highlightColor}
        mask={maskForBlend(intensityHighlight, softenSigma, 'Highlight')}
        background="bevelBase"
        mode={depth.highlightBlendMode}
        blendResult="dodgedFill"
        result="highlight"
      />
      <feComposite
        in="highlight"
        in2="SourceGraphic"
        operator="atop"
        result="litFill"
      />
      <feComponentTransfer in="litFill" result="shadowBase">
        <feFuncA type="table" tableValues="1 1" />
      </feComponentTransfer>
      <feComposite
        in="shadowMask"
        in2="bevelCoverage"
        operator="in"
        result="coveredShadow"
      />
      <TextEffectMaskedBlend
        useIntensity={useIntensity}
        color={depth.shadowColor}
        mask={maskForBlend(intensityShadow, softenSigma, 'Shadow')}
        background="shadowBase"
        mode={depth.shadowBlendMode}
        blendResult="burnedFill"
        result="shadow"
      />
      <feComposite
        in="shadow"
        in2="litFill"
        operator="atop"
        result={preserveInterior ? 'shadedFill' : undefined}
      />
      {preserveInterior && (
        <>
          <feComposite
            in="bevelBase"
            in2="translucentInterior"
            operator="in"
            result="unshadedInteriorPaint"
          />
          <feComposite
            in="unshadedInteriorPaint"
            in2="shadedFill"
            operator="atop"
          />
        </>
      )}
    </filter>
  );
}
